import { useCallback } from 'react';
import type { SequenceXmpMetadataInput } from '@frameio/graphql-client/generated';
import { useSaveExportXmpMetadataToSequenceMutation } from '@frameio/shared/integrations/gql';
import { useCommentMarkerSyncStatusMigrationMutation } from '../../shared/hooks';

const context = {
  resolveWithUXP: true,
};

type SaveExportXmpMetadataArgs = {
  /** The ID of the asset associated with the sequence. */
  assetId: string;
  /** The ID of the sequence to save metadata to. */
  sequenceId: string;
  /** Whether to include in/out timestamps in the metadata. */
  includeInOutTimestamps: boolean;
  /** The XMP metadata object to save. */
  xmpMetadata: SequenceXmpMetadataInput;
  /** A callback to be invoked when the metadata is successfully saved. */
  onCompleted?: () => void | Promise<void>;
};

/**
 * A hook that provides a function to save XMP metadata to a Premiere Pro sequence.
 *
 * After the metadata is successfully saved, it triggers a side effect to update
 * marker sync status for the associated asset (legacy or agnostic GraphQL), with
 * an optimistic response for a faster UI update.
 */
export const useSaveExportMetadata = () => {
  const [saveExportXmpMetadata] = useSaveExportXmpMetadataToSequenceMutation({
    context,
  });
  const { setCommentMarkerSyncStatusForDocument } =
    useCommentMarkerSyncStatusMigrationMutation();

  const handleSave = useCallback(
    async ({
      assetId,
      sequenceId,
      includeInOutTimestamps,
      xmpMetadata,
      onCompleted,
    }: SaveExportXmpMetadataArgs) => {
      return saveExportXmpMetadata({
        variables: {
          sequenceId,
          includeInOutTimestamps,
          xmpMetadata,
        },
        refetchQueries: ['GetAllExportsXmpMetadata'],
        onCompleted: async () => {
          await setCommentMarkerSyncStatusForDocument({
            assetId,
            documentId: sequenceId,
            status: 'ENABLED',
          });

          await onCompleted?.();
        },
      });
    },
    [saveExportXmpMetadata, setCommentMarkerSyncStatusForDocument]
  );

  return handleSave;
};
